import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { Button } from '../button';
import { Modal } from '../modal';
import { ACCEPT_PENDING_POLICIES, MY_PENDING_POLICIES } from '../../services/policyService/queries';
import { graphqlErrorMessage } from '../../utils/listingHold';

type PendingRow = {
  kind: string;
  title: string;
  version: { id: string; versionNumber?: number | null; effectiveDate?: string | null };
};

/**
 * After sign-in, a material policy change asks for acceptance again.
 * Admin routes stay open so an admin can reach /admin/policies; the dialog
 * waits for the next page that is not under /admin.
 */
export const PendingPoliciesModal: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useSnapshot(authStore);
  const onAdmin = location.pathname.startsWith('/admin');
  const { data, refetch } = useQuery(MY_PENDING_POLICIES, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const pending = (data?.myPendingPolicies ?? []) as PendingRow[];
  const [accept, { loading }] = useMutation(ACCEPT_PENDING_POLICIES);
  const [dismissed, setDismissed] = useState(false);

  const open = isAuthenticated && !onAdmin && !dismissed && pending.length > 0;

  const confirm = async () => {
    try {
      await accept({
        variables: {
          input: { versionIds: pending.map(row => row.version.id), platform: 'web' },
        },
      });
      setDismissed(true);
      await refetch();
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'These policies could not be accepted'));
    }
  };

  return (
    <Modal isOpen={open} onClose={() => setDismissed(true)} title="Updated policies">
      <p tw="text-sm mb-3">These documents changed since you accepted them. Please review them.</p>
      <ul tw="text-sm mb-4">
        {pending.map(row => (
          <li key={row.version.id}>
            {row.title}
            {row.version.versionNumber != null ? ` · Version ${row.version.versionNumber}` : ''}
          </li>
        ))}
      </ul>
      <div tw="flex justify-end">
        <Button
          text="I agree"
          type="button"
          isLoading={loading}
          onClick={confirm}
          dataTestId="pending-policies-agree"
        />
      </div>
    </Modal>
  );
};
